import { useEffect, useMemo, useState } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { message, Popover } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../api/ApiRoutes";
import RebetLoader from "../../CommonComponents/RebetLoader/RebetLoader";
import ToggleSwitch from "../../CommonComponents/ToggleSwitch/ToggleSwitch";
import { AppStrings } from "../../../utils/constants/AppStrings";

import "./stylesReferralBonusConfig.css";

const POPOVER_STYLES = {
  root: {
    maxWidth: 300,
    textAlign: "center",
    fontFamily: "Poppins-regular",
  },
};

const DEFAULT_STATE = {
  qualification: {
    minimum_required_purchase: "0",
  },
  sportsbook: {
    free_pick_bonus_amount: "0",
    minimum_odds: "0",
    minimum_number_of_parlay_legs: "0",
  },
  casino: {
    casino_cash_bonus_amount: "0",
  },
};

const QUERY_KEY = ["BONUS_CONFIGS", "REFERRAL_BONUS_CONFIG"];
const isValidDecimal = (value) => /^\d*\.?\d*$/.test(value);
const isValidInteger = (value) => /^\d*$/.test(value);

function ReferralBonusConfig() {
  const token = JSON.parse(localStorage.getItem("token"));
  const privateAdminPanelInstance = createPrivateAdminPanelInstance(token?.AccessToken);
  const queryClient = useQueryClient();

  const [config, setConfig] = useState(DEFAULT_STATE);
  const [initialConfig, setInitialConfig] = useState(DEFAULT_STATE);
  const [bannerText, setBannerText] = useState("");
  const [initialBannerText, setInitialBannerText] = useState("");
  const [availableTags, setAvailableTags] = useState({});
  const [bannerValidationError, setBannerValidationError] = useState(null);
  const [enabled, setEnabled] = useState(true);

  const { data: fetchedConfig, isLoading: isConfigLoading, isError: isConfigError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const response = await privateAdminPanelInstance.get(API_ROUTES.FETCH_V2_REFERRAL_BONUS_CONFIG);
      const payload = response?.data?.data ?? {};

      return {
        enabled: !(payload?.disabled ?? false),
        qualification: {
          minimum_required_purchase: String(payload?.qualification?.minimum_required_purchase ?? "0"),
        },
        sportsbook: {
          free_pick_bonus_amount: String(payload?.sportsbook?.free_pick_bonus_amount ?? "0"),
          minimum_odds: String(payload?.sportsbook?.minimum_odds ?? "0"),
          minimum_number_of_parlay_legs: String(
            payload?.sportsbook?.minimum_number_of_parlay_legs ?? "0"
          ),
        },
        casino: {
          casino_cash_bonus_amount: String(payload?.casino?.casino_cash_bonus_amount ?? "0"),
        },
        bannerTextTemplate: payload?.banner_text_template ?? "",
        availableTags: payload?.available_tags ?? {},
      };
    },
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });

  const updateMutation = useMutation({
    mutationFn: async (payload) =>
      privateAdminPanelInstance.patch(API_ROUTES.UPDATE_V2_REFERRAL_BONUS_CONFIG, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      message.success(AppStrings.VALUES_UPDATED_SUCCESSFULLY);
    },
    onError: () => {
      message.error(AppStrings.FAILED_TO_UPDATE_VALUES);
    },
  });

  useEffect(() => {
    if (!fetchedConfig) {
      return;
    }
    setConfig({
      qualification: fetchedConfig.qualification,
      sportsbook: fetchedConfig.sportsbook,
      casino: fetchedConfig.casino,
    });
    setInitialConfig({
      qualification: fetchedConfig.qualification,
      sportsbook: fetchedConfig.sportsbook,
      casino: fetchedConfig.casino,
    });
    setBannerText(fetchedConfig.bannerTextTemplate);
    setInitialBannerText(fetchedConfig.bannerTextTemplate);
    setAvailableTags(fetchedConfig.availableTags);
    setEnabled(fetchedConfig.enabled);
  }, [fetchedConfig]);

  useEffect(() => {
    if (isConfigError) {
      message.error(AppStrings.FAILED_TO_FETCH_VALUES);
    }
  }, [isConfigError]);

  useEffect(() => {
    const matches = Array.from(bannerText.matchAll(/\{(\w+)\}/g));
    const foundTags = new Set(matches.map((match) => match[1]));
    const backendSupportedTags = Object.keys(availableTags ?? {});
    const unsupportedTags = Array.from(foundTags).filter((tag) => !backendSupportedTags.includes(tag));

    if (unsupportedTags.length > 0) {
      const formattedTags = unsupportedTags.map((tag) => `{${tag}}`).join(", ");
      setBannerValidationError(`${AppStrings.BANNER_UNSUPPORTED_TAGS}${formattedTags}`);
      return;
    }

    setBannerValidationError(null);
  }, [bannerText, availableTags]);

  const isBannerDirty = useMemo(
    () => bannerText !== initialBannerText,
    [bannerText, initialBannerText]
  );
  const isQualificationDirty = useMemo(
    () =>
      config.qualification.minimum_required_purchase !==
      initialConfig.qualification.minimum_required_purchase,
    [config.qualification, initialConfig.qualification]
  );
  const isSportsbookDirty = useMemo(
    () =>
      config.sportsbook.free_pick_bonus_amount !== initialConfig.sportsbook.free_pick_bonus_amount ||
      config.sportsbook.minimum_odds !== initialConfig.sportsbook.minimum_odds ||
      config.sportsbook.minimum_number_of_parlay_legs !==
        initialConfig.sportsbook.minimum_number_of_parlay_legs,
    [config.sportsbook, initialConfig.sportsbook]
  );
  const isCasinoDirty = useMemo(
    () => config.casino.casino_cash_bonus_amount !== initialConfig.casino.casino_cash_bonus_amount,
    [config.casino, initialConfig.casino]
  );

  const handleResetQualification = () => {
    setConfig((prev) => ({
      ...prev,
      qualification: { ...initialConfig.qualification },
    }));
  };

  const handleResetSportsbook = () => {
    setConfig((prev) => ({
      ...prev,
      sportsbook: { ...initialConfig.sportsbook },
    }));
  };

  const handleResetCasino = () => {
    setConfig((prev) => ({
      ...prev,
      casino: { ...initialConfig.casino },
    }));
  };

  const handleResetBanner = () => {
    setBannerText(initialBannerText);
  };

  const updateField = (section, key, value) => {
    const isLegsField = section === "sportsbook" && key === "minimum_number_of_parlay_legs";
    const isValid = isLegsField ? isValidInteger(value) : isValidDecimal(value);
    if (!isValid) {
      return;
    }

    setConfig((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const handleSaveQualification = () => {
    updateMutation.mutate({
      qualification: {
        minimum_required_purchase: parseFloat(config.qualification.minimum_required_purchase),
      },
    });
  };

  const handleSaveSportsbook = () => {
    updateMutation.mutate({
      sportsbook: {
        free_pick_bonus_amount: parseFloat(config.sportsbook.free_pick_bonus_amount),
        minimum_odds: parseFloat(config.sportsbook.minimum_odds),
        minimum_number_of_parlay_legs: parseInt(config.sportsbook.minimum_number_of_parlay_legs, 10),
      },
    });
  };

  const handleSaveCasino = () => {
    updateMutation.mutate({
      casino: {
        casino_cash_bonus_amount: parseFloat(config.casino.casino_cash_bonus_amount),
      },
    });
  };

  const handleSaveBanner = () => {
    updateMutation.mutate({
      banner_text_template: bannerText,
    });
  };

  const handleEnabledChange = (value) => {
    const nextEnabled = typeof value === "boolean" ? value : value.is_active;
    const previousEnabled = enabled;
    setEnabled(nextEnabled);
    updateMutation.mutate(
      {
        disabled: !nextEnabled,
      },
      {
        onError: () => setEnabled(previousEnabled),
      }
    );
  };

  const isApiRunning =
    isConfigLoading ||
    updateMutation.isPending;

  return (
    <div className={`bonus-configs-layout ${isApiRunning ? "loading" : ""}`}>
      <div className="bonus-configs-columns">
        <div className="bonus-configs-column bonus-configs-column-left">
          <div className="bonus-configs-card">
            <h3>{AppStrings.REFERRAL_BONUS}</h3>
            <div className="bonus-configs-toggle-row">
              <label>{AppStrings.REFERRAL_BONUS_ENABLED}</label>
              <ToggleSwitch
                status={enabled}
                eventCode="referral-bonus-enabled"
                updateEventCode={handleEnabledChange}
                onlyUpdate={false}
                disabled={isApiRunning}
              />
            </div>
          </div>

          <div className="bonus-configs-card">
            <h3>{AppStrings.SPORTSBOOK}</h3>
            <div className="bonus-configs-field">
              <label htmlFor="referral-sb-free-pick-bonus-amount">
                {AppStrings.FREE_PICK_BONUS_AMOUNT}
                <Popover content={AppStrings.REFERRAL_FREE_PICK_BONUS_TOOLTIP} trigger="hover" placement="topLeft" styles={POPOVER_STYLES}>
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </label>
              <input
                id="referral-sb-free-pick-bonus-amount"
                type="text"
                inputMode="decimal"
                value={config.sportsbook.free_pick_bonus_amount}
                onChange={(e) => updateField("sportsbook", "free_pick_bonus_amount", e.target.value)}
                disabled={isApiRunning}
              />
            </div>
            <div className="bonus-configs-field">
              <label htmlFor="referral-sb-minimum-odds">
                {AppStrings.MINIMUM_ODDS}
                <Popover content={AppStrings.REFERRAL_MIN_ODDS_TOOLTIP} trigger="hover" placement="topLeft" styles={POPOVER_STYLES}>
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </label>
              <input
                id="referral-sb-minimum-odds"
                type="text"
                inputMode="decimal"
                value={config.sportsbook.minimum_odds}
                onChange={(e) => updateField("sportsbook", "minimum_odds", e.target.value)}
                disabled={isApiRunning}
              />
            </div>
            <div className="bonus-configs-field">
              <label htmlFor="referral-sb-minimum-legs">
                {AppStrings.MINIMUM_PARLAY_LEGS}
                <Popover content={AppStrings.REFERRAL_MIN_LEGS_TOOLTIP} trigger="hover" placement="topLeft" styles={POPOVER_STYLES}>
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </label>
              <input
                id="referral-sb-minimum-legs"
                type="text"
                inputMode="numeric"
                value={config.sportsbook.minimum_number_of_parlay_legs}
                onChange={(e) => updateField("sportsbook", "minimum_number_of_parlay_legs", e.target.value)}
                disabled={isApiRunning}
              />
            </div>
            <div className="bonus-configs-actions">
              <button type="button" onClick={handleResetSportsbook} disabled={!isSportsbookDirty || isApiRunning}>
                {AppStrings.RESET}
              </button>
              <button type="button" onClick={handleSaveSportsbook} disabled={!isSportsbookDirty || isApiRunning}>
                {isApiRunning ? <RebetLoader height="20px" width="20px" /> : AppStrings.SAVE_CHANGES}
              </button>
            </div>
          </div>

          <div className="bonus-configs-card">
            <h3>{AppStrings.CASINO}</h3>
            <div className="bonus-configs-field">
              <label htmlFor="referral-casino-cash-bonus-amount">
                {AppStrings.CASINO_CASH_BONUS_AMOUNT}
                <Popover content={AppStrings.REFERRAL_CASINO_CASH_TOOLTIP} trigger="hover" placement="topLeft" styles={POPOVER_STYLES}>
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </label>
              <input
                id="referral-casino-cash-bonus-amount"
                type="text"
                inputMode="decimal"
                value={config.casino.casino_cash_bonus_amount}
                onChange={(e) => updateField("casino", "casino_cash_bonus_amount", e.target.value)}
                disabled={isApiRunning}
              />
            </div>
            <div className="bonus-configs-actions">
              <button type="button" onClick={handleResetCasino} disabled={!isCasinoDirty || isApiRunning}>
                {AppStrings.RESET}
              </button>
              <button type="button" onClick={handleSaveCasino} disabled={!isCasinoDirty || isApiRunning}>
                {isApiRunning ? <RebetLoader height="20px" width="20px" /> : AppStrings.SAVE_CHANGES}
              </button>
            </div>
          </div>
        </div>

        <div className="bonus-configs-column bonus-configs-column-right">
          <div className="bonus-configs-card">
            <h3>{AppStrings.QUALIFICATION}</h3>
            <div className="bonus-configs-field">
              <label htmlFor="referral-min-required-purchase">
                {AppStrings.MINIMUM_REQUIRED_PURCHASE}
                <Popover content={AppStrings.REFERRAL_MIN_REQUIRED_PURCHASE_TOOLTIP} trigger="hover" placement="topLeft" styles={POPOVER_STYLES}>
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </label>
              <input
                id="referral-min-required-purchase"
                type="text"
                inputMode="decimal"
                value={config.qualification.minimum_required_purchase}
                onChange={(e) => updateField("qualification", "minimum_required_purchase", e.target.value)}
                disabled={isApiRunning}
              />
            </div>
            <div className="bonus-configs-actions">
              <button type="button" onClick={handleResetQualification} disabled={!isQualificationDirty || isApiRunning}>
                {AppStrings.RESET}
              </button>
              <button type="button" onClick={handleSaveQualification} disabled={!isQualificationDirty || isApiRunning}>
                {isApiRunning ? <RebetLoader height="20px" width="20px" /> : AppStrings.SAVE_CHANGES}
              </button>
            </div>
          </div>

          <div className="bonus-configs-card">
            <h3>{AppStrings.BANNER_TEXT}</h3>
            <div className="bonus-configs-field">
              <label htmlFor="referral-banner-text-template">
                {AppStrings.BANNER_TEXT_TEMPLATE}
                <Popover content={AppStrings.BANNER_TEXT_TOOLTIP} trigger="hover" placement="topLeft" styles={POPOVER_STYLES}>
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </label>
              <textarea
                id="referral-banner-text-template"
                rows={3}
                value={bannerText}
                onChange={(e) => setBannerText(e.target.value)}
                disabled={isApiRunning}
              />
              {bannerValidationError ? <span className="validation-error">{bannerValidationError}</span> : null}
            </div>

            <div className="bonus-configs-field">
              <label>{AppStrings.BANNER_AVAILABLE_TAGS}</label>
              <div className="bonus-configs-tags-ref">
                {Object.entries(availableTags ?? {}).map(([tagName, tagValue]) => (
                  <span key={tagName}>
                    <code>{`{${tagName}}`}</code> = {String(tagValue ?? "-")}
                  </span>
                ))}
              </div>
            </div>

            <div className="bonus-configs-actions">
              <button type="button" onClick={handleResetBanner} disabled={!isBannerDirty || isApiRunning}>
                {AppStrings.RESET}
              </button>
              <button
                type="button"
                onClick={handleSaveBanner}
                disabled={!isBannerDirty || isApiRunning || Boolean(bannerValidationError)}
              >
                {isApiRunning ? <RebetLoader height="20px" width="20px" /> : AppStrings.SAVE_CHANGES}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReferralBonusConfig;
